<template>
  <div style="background:#f4f4f6; min-height:100vh;" class="py-4 py-md-5">

    <!-- Loading -->
    <div v-if="loading" class="d-flex justify-content-center align-items-center" style="min-height:60vh;">
      <div class="text-center">
        <div class="spinner-border text-primary mb-3" role="status" style="width:2.5rem;height:2.5rem;"></div>
        <p class="text-muted small">Loading statement…</p>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="d-flex justify-content-center align-items-center" style="min-height:60vh;">
      <div class="text-center px-3">
        <i class="bi bi-exclamation-circle text-danger" style="font-size:3rem;"></i>
        <h5 class="mt-3">{{ error }}</h5>
        <p class="text-muted small">Please contact the business if you believe this is a mistake.</p>
      </div>
    </div>

    <!-- Content -->
    <div v-else class="container" style="max-width:920px;">

      <div class="bg-white rounded shadow-sm overflow-hidden mb-3">

        <!-- Company header -->
        <div class="text-center px-4 pt-4 pb-3" :style="`border-bottom:3px solid ${accentColor};`">
          <img
            v-if="company.show_logo && company.logo_url"
            :src="company.logo_url"
            style="max-height:70px; width:auto; object-fit:contain;"
            class="mb-2"
          />
          <div v-else class="mb-2">
            <div
              class="d-inline-flex align-items-center justify-content-center rounded-circle text-white font-weight-bold"
              :style="`width:56px;height:56px;font-size:22px;background:${accentColor};`"
            >{{ company.name ? company.name.charAt(0).toUpperCase() : '?' }}</div>
          </div>
          <h5 class="font-weight-bold mb-1">{{ company.name }}</h5>
          <p class="text-muted mb-0" style="font-size:13px;">
            <span v-if="company.phone"><i class="bi bi-telephone mr-1"></i>{{ company.phone }}</span>
            <span v-if="company.phone && (company.address || company.email)"> &nbsp;&bull;&nbsp; </span>
            <span v-if="company.email"><i class="bi bi-envelope mr-1"></i>{{ company.email }}</span>
            <span v-if="company.email && company.address"> &nbsp;&bull;&nbsp; </span>
            <span v-if="company.address">{{ company.address }}<span v-if="company.town">, {{ company.town }}</span></span>
          </p>
        </div>

        <!-- Statement title + customer + download/print -->
        <div class="d-flex justify-content-between align-items-start flex-wrap px-4 py-3" style="border-bottom:1px solid #eee;gap:10px;">
          <div>
            <p class="text-uppercase text-muted mb-1" style="font-size:11px;letter-spacing:.08em;">Account Statement</p>
            <h6 class="font-weight-bold mb-0">{{ customerLabel }}</h6>
            <small class="text-muted d-block" v-if="customer.company && customer.full_name">Attn: {{ customer.full_name }}</small>
            <small class="text-muted" v-if="customer.phone">{{ customer.phone }}</small>
            <small class="text-muted d-block" v-if="customer.full_address">{{ customer.full_address }}</small>
          </div>
          <div class="d-flex" style="gap:8px;">
            <button @click="downloadStatementPdf" :disabled="downloadingPdf" class="btn btn-sm btn-outline-secondary">
              <span v-if="downloadingPdf" class="spinner-border spinner-border-sm mr-1"></span>
              <i v-else class="bi bi-download mr-1"></i> PDF
            </button>
            <button @click="printStatement" :disabled="printingPdf" class="btn btn-sm btn-outline-secondary">
              <span v-if="printingPdf" class="spinner-border spinner-border-sm mr-1"></span>
              <i v-else class="bi bi-printer mr-1"></i> Print
            </button>
          </div>
        </div>

        <!-- Summary stats -->
        <div class="row text-center no-gutters" style="border-bottom:1px solid #eee;">
          <div class="col-6 col-md-3 py-3 stat-cell">
            <p class="font-weight-bold mb-0" :style="`font-size:16px;color:${accentColor};`">{{ fmtAmount(summary.total_outstanding) }}</p>
            <small class="text-muted">Total Outstanding</small>
          </div>
          <div class="col-6 col-md-3 py-3 stat-cell">
            <p class="font-weight-bold text-dark mb-0" style="font-size:16px;">{{ fmtAmount(summary.total_invoiced) }}</p>
            <small class="text-muted">Total Invoiced</small>
          </div>
          <div class="col-6 col-md-3 py-3 stat-cell">
            <p class="font-weight-bold text-dark mb-0" style="font-size:16px;">{{ fmtAmount(summary.total_ordered) }}</p>
            <small class="text-muted">Total Ordered</small>
          </div>
          <div class="col-6 col-md-3 py-3 stat-cell">
            <p class="font-weight-bold text-dark mb-0" style="font-size:16px;">{{ fmtAmount(summary.open_quotes_value) }}</p>
            <small class="text-muted">Open Quotes</small>
          </div>
        </div>

        <!-- Aging widget -->
        <div class="px-4 py-3" style="border-bottom:1px solid #eee;">
          <p class="text-uppercase text-muted mb-2" style="font-size:11px;letter-spacing:.08em;">Invoice Aging</p>
          <div class="row no-gutters">
            <div class="col-4 col-md-2-4 mb-2 pr-2" style="flex:1 0 20%;">
              <div class="aging-tile"><div class="aging-tile__lbl">Current</div><div class="aging-tile__val">{{ fmtAmount(aging.current.amount) }}</div></div>
            </div>
            <div class="col-4 col-md-2-4 mb-2 pr-2" style="flex:1 0 20%;">
              <div class="aging-tile"><div class="aging-tile__lbl">1-30d</div><div class="aging-tile__val">{{ fmtAmount(aging.bucket_1_30.amount) }}</div></div>
            </div>
            <div class="col-4 col-md-2-4 mb-2 pr-2" style="flex:1 0 20%;">
              <div class="aging-tile"><div class="aging-tile__lbl">31-60d</div><div class="aging-tile__val">{{ fmtAmount(aging.bucket_31_60.amount) }}</div></div>
            </div>
            <div class="col-6 col-md-2-4 mb-2 pr-2" style="flex:1 0 20%;">
              <div class="aging-tile"><div class="aging-tile__lbl">61-90d</div><div class="aging-tile__val">{{ fmtAmount(aging.bucket_61_90.amount) }}</div></div>
            </div>
            <div class="col-6 col-md-2-4 mb-2" style="flex:1 0 20%;">
              <div class="aging-tile aging-tile--danger"><div class="aging-tile__lbl">90+d</div><div class="aging-tile__val">{{ fmtAmount(aging.bucket_90_plus.amount) }}</div></div>
            </div>
          </div>
        </div>

        <!-- Spend chart -->
        <div class="px-4 py-3" style="border-bottom:1px solid #eee;">
          <p class="text-uppercase text-muted mb-2" style="font-size:11px;letter-spacing:.08em;">Orders vs Invoices — Last 12 Months</p>
          <div class="position-relative" style="height:220px;">
            <canvas ref="chartCanvas"></canvas>
          </div>
        </div>

        <!-- Orders -->
        <div class="px-4 py-3" style="border-bottom:1px solid #eee;">
          <p class="text-uppercase text-muted mb-2" style="font-size:11px;letter-spacing:.08em;">Orders</p>
          <div v-if="orders.transactions.length" class="table-responsive">
            <table class="table table-sm statement-table mb-0">
              <thead><tr><th>Date</th><th>Type</th><th>Ref</th><th class="text-right">Amount</th><th class="text-right">Balance</th></tr></thead>
              <tbody>
                <tr v-for="(line, i) in orders.transactions" :key="'o'+i">
                  <td>{{ fmtDate(line.date) }}</td>
                  <td>{{ line.type }}</td>
                  <td>{{ line.reference }}</td>
                  <td class="text-right">{{ line.debit > 0 ? fmtAmount(line.debit) : '-' + fmtAmount(line.credit) }}</td>
                  <td class="text-right font-weight-bold">{{ fmtAmount(line.balance) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-muted small mb-0">No orders yet.</p>
        </div>

        <!-- Invoices -->
        <div class="px-4 py-3" style="border-bottom:1px solid #eee;">
          <p class="text-uppercase text-muted mb-2" style="font-size:11px;letter-spacing:.08em;">Invoices</p>
          <div v-if="invoices.transactions.length" class="table-responsive">
            <table class="table table-sm statement-table mb-0">
              <thead><tr><th>Date</th><th>Type</th><th>Ref</th><th class="text-right">Amount</th><th class="text-right">Balance</th></tr></thead>
              <tbody>
                <tr v-for="(line, i) in invoices.transactions" :key="'i'+i">
                  <td>{{ fmtDate(line.date) }}</td>
                  <td>{{ line.type }}</td>
                  <td>{{ line.reference }}</td>
                  <td class="text-right">{{ line.debit > 0 ? fmtAmount(line.debit) : '-' + fmtAmount(line.credit) }}</td>
                  <td class="text-right font-weight-bold">{{ fmtAmount(line.balance) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-muted small mb-0">No invoices yet.</p>
        </div>

        <!-- Quotes -->
        <div class="px-4 py-3" style="border-bottom:1px solid #eee;">
          <p class="text-uppercase text-muted mb-1" style="font-size:11px;letter-spacing:.08em;">Open Proposals (Quotes)</p>
          <p class="text-muted mb-2" style="font-size:11.5px;">Quotes are proposals only and are not included in the balances above.</p>
          <div v-if="quotes.rows.length" class="table-responsive">
            <table class="table table-sm statement-table mb-0">
              <thead><tr><th>Number</th><th>Date</th><th>Status</th><th class="text-right">Amount</th></tr></thead>
              <tbody>
                <tr v-for="q in quotes.rows" :key="q.id">
                  <td>{{ q.number }}</td>
                  <td>{{ fmtDate(q.date) }}</td>
                  <td><span class="badge" :class="quoteStatusMeta(q.status).badge">{{ quoteStatusMeta(q.status).label }}</span></td>
                  <td class="text-right">{{ fmtAmount(q.total_amount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-muted small mb-0">No quotes on file.</p>
        </div>

        <!-- Payment history -->
        <div class="px-4 py-3" style="border-bottom:1px solid #eee;" v-if="insights.payment_history.length">
          <p class="text-uppercase text-muted mb-2" style="font-size:11px;letter-spacing:.08em;">Payment History</p>
          <div class="table-responsive">
            <table class="table table-sm statement-table mb-0">
              <thead><tr><th>Date</th><th>Method</th><th>Order</th><th class="text-right">Amount</th></tr></thead>
              <tbody>
                <tr v-for="(p, i) in insights.payment_history" :key="'p'+i">
                  <td>{{ fmtDate(p.date) }}</td>
                  <td>{{ p.method }}</td>
                  <td>{{ p.order_number }}</td>
                  <td class="text-right">{{ fmtAmount(p.amount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Product insights -->
        <div class="px-4 py-3" v-if="insights.product_insights.length">
          <p class="text-uppercase text-muted mb-2" style="font-size:11px;letter-spacing:.08em;">Frequently Purchased</p>
          <div class="table-responsive">
            <table class="table table-sm statement-table mb-0">
              <thead><tr><th>Product</th><th class="text-right">Qty</th><th class="text-right">Spent</th><th>Last Purchased</th></tr></thead>
              <tbody>
                <tr v-for="p in insights.product_insights" :key="p.product_id">
                  <td>{{ p.product_name }}</td>
                  <td class="text-right">{{ p.total_qty }}</td>
                  <td class="text-right">{{ fmtAmount(p.total_spent) }}</td>
                  <td>{{ p.last_purchased_at ? fmtDate(p.last_purchased_at) : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Footer inside doc -->
        <div class="text-center py-3" style="background:#f8f8f8;border-top:1px solid #eee;">
          <small class="text-muted">
            <a :href="$platformBrand.websiteUrl" class="text-muted" style="text-decoration:none;">
              Powered by <strong>{{ $platformBrand.name }}</strong>
            </a>
          </small>
        </div>

      </div>

      <!-- ── Not logged in: Explore Dukakit banner ── -->
      <div v-if="!isLoggedIn" class="bg-white rounded shadow-sm px-4 py-4 mb-3 text-center">
        <div class="mb-2">
          <i class="bi bi-shop text-primary" style="font-size:2rem;"></i>
        </div>
        <h6 class="font-weight-bold mb-1">Explore more out of {{ $platformBrand.name }}</h6>
        <p class="text-muted mb-3" style="font-size:13px;">
          Sign up or log in to view your transaction history across <strong>all the businesses</strong> that have your number — in one place.
        </p>
        <a href="/register" class="btn btn-primary btn-sm mr-2">Create Account</a>
        <a href="/login" class="btn btn-outline-secondary btn-sm">Log In</a>
      </div>

      <!-- ── Logged in, no phone: nudge to add phone ── -->
      <div v-if="isLoggedIn && !userDetails.phone" class="bg-white rounded shadow-sm px-4 py-4 mb-3 text-center">
        <i class="bi bi-telephone-plus text-primary" style="font-size:2rem;"></i>
        <h6 class="font-weight-bold mt-2 mb-1">Link your purchases across all businesses</h6>
        <p class="text-muted mb-3" style="font-size:13px;">
          Add a phone number to your profile and we'll show you every shop on {{ $platformBrand.name }} that has captured your number.
        </p>
        <a href="/profile" class="btn btn-outline-primary btn-sm">Update My Profile</a>
      </div>

      <!-- ── Logged in with phone: portal ── -->
      <div v-if="isLoggedIn && userDetails.phone">

        <div v-if="portalLoading" class="text-center py-3">
          <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
          <small class="text-muted ml-2">Loading your businesses…</small>
        </div>

        <div v-else-if="portalData.length > 0">
          <p class="text-uppercase text-muted mb-2 px-1" style="font-size:11px;letter-spacing:.08em;">Your Activity Across Businesses</p>
          <div class="row no-gutters" style="gap:0;">
            <div
              class="col-12 col-sm-6 mb-3 pr-sm-2"
              v-for="biz in portalData"
              :key="biz.customer_id"
            >
              <div class="bg-white rounded shadow-sm h-100" style="overflow:hidden;">
                <div class="d-flex align-items-center px-3 py-3" style="border-bottom:1px solid #f0f0f0;">
                  <div
                    v-if="!biz.logo"
                    class="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary text-white font-weight-bold flex-shrink-0 mr-2"
                    style="width:36px;height:36px;font-size:14px;"
                  >{{ biz.company.charAt(0).toUpperCase() }}</div>
                  <img
                    v-else
                    :src="biz.logo"
                    style="height:36px;width:36px;object-fit:contain;border-radius:4px;"
                    class="mr-2 flex-shrink-0"
                  />
                  <div class="overflow-hidden">
                    <p class="font-weight-bold mb-0 text-truncate" style="font-size:14px;">{{ biz.company }}</p>
                    <small class="text-muted" v-if="biz.last_visit">Last visit {{ fmtDateRelative(biz.last_visit) }}</small>
                  </div>
                </div>
                <div class="row text-center no-gutters px-2 py-2">
                  <div class="col-4">
                    <p class="font-weight-bold mb-0">{{ biz.order_count }}</p>
                    <small class="text-muted" style="font-size:10px;">Orders</small>
                  </div>
                  <div class="col-4">
                    <p class="font-weight-bold text-dark mb-0" style="font-size:12px;">{{ fmtAmount(biz.total_paid) }}</p>
                    <small class="text-muted" style="font-size:10px;">Spent</small>
                  </div>
                  <div class="col-4">
                    <p class="font-weight-bold mb-0" :class="biz.total_balance > 0 ? 'text-danger' : 'text-primary'" style="font-size:12px;">
                      {{ fmtAmount(biz.total_balance) }}
                    </p>
                    <small class="text-muted" style="font-size:10px;">Balance</small>
                  </div>
                </div>
                <div class="px-3 pb-3 text-right">
                  <a :href="'/customer-statement/' + biz.customer_id" class="btn btn-sm btn-outline-secondary" style="font-size:12px;">
                    View Statement <i class="bi bi-arrow-right"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="bg-white rounded shadow-sm px-4 py-4 mb-3 text-center">
          <i class="bi bi-shop text-muted" style="font-size:2rem;"></i>
          <p class="text-muted mt-2 mb-0" style="font-size:13px;">
            No other businesses found linked to <strong>{{ userDetails.phone }}</strong> yet.
          </p>
        </div>

      </div>

    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { Chart, registerables } from 'chart.js';
import { quoteStatusMeta, invoiceStatusMeta } from '../quotesInvoices/docHelpers';
import { downloadPdf, printPdf } from '../../services/pdfDownload';

Chart.register(...registerables);

export default {
  name: 'customer-statement-view',
  props: ['customerId'],

  data() {
    return {
      loading: true,
      portalLoading: false,
      downloadingPdf: false,
      printingPdf: false,
      error: null,
      chart: null,
      customer: {},
      company: {},
      orders: { opening_balance: 0, closing_balance: 0, transactions: [] },
      invoices: { opening_balance: 0, closing_balance: 0, transactions: [] },
      quotes: { open_count: 0, open_value: 0, rows: [] },
      aging: {
        current: { amount: 0 }, bucket_1_30: { amount: 0 }, bucket_31_60: { amount: 0 },
        bucket_61_90: { amount: 0 }, bucket_90_plus: { amount: 0 },
      },
      insights: { product_insights: [], payment_history: [] },
      summary: { total_outstanding: 0, total_invoiced: 0, total_ordered: 0, open_quotes_value: 0 },
      chartSeries: [],
      portalData: [],
    };
  },

  computed: {
    ...mapGetters(['userDetails']),
    isLoggedIn() {
      return !!(this.userDetails && this.userDetails.id);
    },
    accentColor() {
      return this.company.accent_color || '#fd4719';
    },
    customerLabel() {
      return (this.customer.company || this.customer.full_name || 'Walk-in customer');
    },
    pdfUrl() {
      return `/api/public/customer-statement/pdf?customer_id=${this.customerId}`;
    },
  },

  mounted() {
    this.loadStatement();
  },

  beforeUnmount() {
    if (this.chart) this.chart.destroy();
  },

  methods: {
    quoteStatusMeta,
    invoiceStatusMeta,

    async loadStatement() {
      try {
        const res = await axios.get('/api/public/customer-statement', {
          params: { customer_id: this.customerId },
        });
        this.customer  = res.data.customer;
        this.company   = res.data.company;
        this.orders    = res.data.orders;
        this.invoices  = res.data.invoices;
        this.quotes    = res.data.quotes;
        this.aging     = res.data.aging;
        this.insights  = res.data.insights;
        this.summary   = res.data.summary;
        this.chartSeries = res.data.chart;

        this.$nextTick(() => this.initChart());

        if (this.isLoggedIn && this.userDetails.phone) {
          this.loadPortal(this.userDetails.phone);
        }
      } catch (e) {
        this.error = 'We could not load this statement.';
      } finally {
        this.loading = false;
      }
    },

    async loadPortal(phone) {
      this.portalLoading = true;
      try {
        const res = await axios.get('/api/public/customer-portal', { params: { phone } });
        this.portalData = res.data;
      } catch (e) {
        // fail silently — portal is supplementary
      } finally {
        this.portalLoading = false;
      }
    },

    initChart() {
      if (!this.$refs.chartCanvas) return;
      const ctx = this.$refs.chartCanvas.getContext('2d');
      this.chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.chartSeries.map(m => moment(m.month + '-01').format('MMM YY')),
          datasets: [
            { label: 'Ordered', data: this.chartSeries.map(m => m.ordered), backgroundColor: '#3b82f6', borderRadius: 4 },
            { label: 'Invoiced', data: this.chartSeries.map(m => m.invoiced), backgroundColor: '#10b981', borderRadius: 4 },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } },
            tooltip: { backgroundColor: 'rgba(0,0,0,0.8)', cornerRadius: 8 },
          },
          scales: {
            x: { grid: { display: false }, ticks: { font: { size: 10 } } },
            y: { beginAtZero: true, ticks: { font: { size: 10 } } },
          },
        },
      });
    },

    async downloadStatementPdf() {
      this.downloadingPdf = true;
      try {
        await downloadPdf(this.pdfUrl, `Statement-${this.customerLabel}.pdf`);
      } catch (e) {
        this.$toasts && this.$toasts.push({ type: 'danger', message: 'Could not download the PDF. Please try again.' });
      } finally {
        this.downloadingPdf = false;
      }
    },

    async printStatement() {
      this.printingPdf = true;
      try {
        await printPdf(this.pdfUrl);
      } catch (e) {
        this.$toasts && this.$toasts.push({ type: 'danger', message: 'Could not print this statement. Please try again.' });
      } finally {
        this.printingPdf = false;
      }
    },

    fmtAmount(amount) {
      if (amount == null) return '—';
      return 'Kshs ' + Number(amount).toLocaleString('en', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    fmtDate(date) {
      return moment(date).format('DD MMM YYYY');
    },

    fmtDateRelative(date) {
      return moment(date).fromNow();
    },
  },
};
</script>

<style scoped>
.stat-cell { border-right: 1px solid #eee; border-bottom: 1px solid #eee; }
.stat-cell:nth-child(2n) { border-right: none; }
@media (min-width: 768px) { .stat-cell { border-bottom: none; } .stat-cell:nth-child(2n) { border-right: 1px solid #eee; } .stat-cell:last-child { border-right: none; } }
.aging-tile { background: #f8f9fa; border-radius: 8px; padding: 8px 6px; text-align: center; }
.aging-tile--danger { background: #f4e8e8; }
.aging-tile__lbl { font-size: 9.5px; text-transform: uppercase; color: #999; }
.aging-tile__val { font-size: 12px; font-weight: bold; color: #333; margin-top: 2px; }
.statement-table th { font-size: 10.5px; text-transform: uppercase; color: #999; border-top: none; font-weight: normal; white-space: nowrap; }
.statement-table td { font-size: 12.5px; vertical-align: middle; }
</style>
